import React, {useState, useEffect} from 'react';
import {
	DataGrid, GridToolbarContainer,
	GridToolbarFilterButton,
	GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import {
	Button, Chip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
	Snackbar, useMediaQuery, Card, CardActionArea, CardContent, Box,
	Typography, Backdrop, CircularProgress,
} from '@mui/material';
import {useNavigate} from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import {styled, ThemeProvider} from '@mui/material/styles';
import dayjs from 'dayjs';
import {useDispatch, useSelector} from 'react-redux';
import {fetchOrdersByDate, selectOrdersByDate} from '../../store/ordersSlice';
import OrderDetails from './OrderDetails';
import {
	ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
	buildAdvancedSearchRequest,
	filterAdvancedResults,
	isAdvancedSearchRequestEmpty,
	normalizeAdvancedSearchResponse,
	sanitizeAdvancedSearchInputs,
} from './advancedSearchUtils';
import {theme} from "../../theme";
import {config} from "../../config";
import {getVendorId, getSessionToken, getUserName} from "../../utils/authSession";
import {isStorePickup} from "../../utils/fulfillment";

const statusLabels = {
	NEW: 'New',
	PROCESSING: 'Processing',
	PICKED: 'Picked',
	PACKED: 'Packed',
	READY_FOR_PICKUP: 'Ready for Pickup',
	PICKED_UP: 'Picked Up',
	SHIPPED: 'Shipped',
	OUT_FOR_DELIVERY: 'Out for Delivery',
	DELIVERED: 'Delivered',
	CANCELLED: 'Cancelled',
	ON_HOLD: 'On Hold',
	RETURNED: 'Returned',
};


function CustomToolbar() {
	return (
		<GridToolbarContainer sx={{p: 1}}>
			<GridToolbarFilterButton color='secondary'/>
			<GridToolbarDensitySelector color='secondary'
			                            slotProps={{tooltip: {title: 'Change density'}}}
			/>
		</GridToolbarContainer>
	);
}

const getStatusStyles = (status) => {
	switch (status) {
		case 'NEW':
			return {backgroundColor: '#2196F3', color: '#FFFFFF'};
		case 'PROCESSING':
			return {backgroundColor: '#FB8C00', color: '#FFFFFF'};
		case 'PICKED':
			return {backgroundColor: '#388E3C', color: '#FFFFFF'};
		case 'PACKED':
			return {backgroundColor: '#7B1FA2', color: '#FFFFFF'};
		case 'READY_FOR_PICKUP':
			return {backgroundColor: '#FBC02D', color: '#FFFFFF'};
		case 'PICKED_UP':
			return {backgroundColor: '#8BC34A', color: '#FFFFFF'};
		case 'SHIPPED':
			return {backgroundColor: '#673AB7', color: '#FFFFFF'};
		case 'OUT_FOR_DELIVERY':
			return {backgroundColor: '#009688', color: '#FFFFFF'};
		case 'DELIVERED':
			return {backgroundColor: '#4CAF50', color: '#FFFFFF'};
		case 'CANCELLED':
			return {backgroundColor: '#F44336', color: '#FFFFFF'};
		case 'ON_HOLD':
			return {backgroundColor: '#FFB300', color: '#FFFFFF'};
		case 'RETURNED':
			return {backgroundColor: '#ff4784', color: '#FFFFFF'};
		default:
			return {backgroundColor: '#9E9E9E', color: '#FFFFFF'};
	}
};

const getNextStatusButtonStyles = (status) => {
	const style = getStatusStyles(status);
	return {
		backgroundColor: style.backgroundColor,
		color: style.color,
		boxShadow: 'none',
		textTransform: 'none',
		fontWeight: 600,
		width: '100%',
		':hover': {
			backgroundColor: style.backgroundColor,
			filter: 'brightness(0.9)',
		},
		borderRadius: '50px',
	};
};

const validateStatusTransition = (currentStatus, newStatus, isPickup) => {
	switch (currentStatus) {
		case 'NEW':
			return ['PROCESSING', 'CANCELLED', 'ON_HOLD'].includes(newStatus);
		case 'PROCESSING':
			return ['PICKED', 'CANCELLED', 'ON_HOLD'].includes(newStatus);
		case 'PICKED':
			return ['PACKED', 'CANCELLED', 'ON_HOLD'].includes(newStatus);
		case 'PACKED':
			if (isPickup === null || isPickup === undefined) {
				return ['CANCELLED', 'ON_HOLD'].includes(newStatus);
			}
			return isPickup
				? ['READY_FOR_PICKUP', 'CANCELLED', 'ON_HOLD'].includes(newStatus)
				: ['SHIPPED', 'CANCELLED', 'ON_HOLD'].includes(newStatus);
		case 'READY_FOR_PICKUP':
			return ['PICKED_UP', 'CANCELLED', 'ON_HOLD'].includes(newStatus);
		case 'SHIPPED':
			return ['OUT_FOR_DELIVERY', 'RETURNED', 'ON_HOLD'].includes(newStatus);
		case 'OUT_FOR_DELIVERY':
			return ['DELIVERED', 'RETURNED', 'ON_HOLD'].includes(newStatus);
		case 'DELIVERED':
			return newStatus === 'RETURNED';
		case 'ON_HOLD':
			return ['PROCESSING', 'CANCELLED'].includes(newStatus);
		case 'CANCELLED':
		case 'RETURNED':
			return false;
		default:
			return false;
	}
};

const getNextAction = (order) => {
	const {orderStatus} = order;
	const pickup = isStorePickup(order);
	let nextStatuses = [];
	switch (orderStatus) {
		case 'NEW':
			nextStatuses = ['PROCESSING'];
			break;
		case 'PROCESSING':
			nextStatuses = ['PICKED'];
			break;
		case 'PICKED':
			nextStatuses = ['PACKED'];
			break;
		case 'PACKED':
			nextStatuses = pickup === null ? [] : pickup ? ['READY_FOR_PICKUP'] : ['SHIPPED'];
			break;
		case 'READY_FOR_PICKUP':
			nextStatuses = ['PICKED_UP'];
			break;
		case 'SHIPPED':
			nextStatuses = ['OUT_FOR_DELIVERY'];
			break;
		case 'OUT_FOR_DELIVERY':
			nextStatuses = ['DELIVERED'];
			break;
		default:
			nextStatuses = [];
	}
	for (const ns of nextStatuses) {
		if (validateStatusTransition(orderStatus, ns, pickup)) {
			return ns;
		}
	}
	return null;
};

const API_BASE_URL = config.API_MWSSTOREFRONT_BASE_URL;

const sessionToken = getSessionToken;
const buildUrl = (path, vendorId) =>
	`${API_BASE_URL}/storefront/orm/${encodeURIComponent(vendorId)}${path}`;
const authHeaders = () => ({'X-Session-Token': sessionToken()});

const OrderList = ({
	                   filters,
	                   searchResults = null,
	                   onAdvancedSearchResultsChange,
	                   searchLoading = false,
                   }) => {
	const navigate = useNavigate();
	const {searchQuery, selectedStatus, selectedDate} = filters;
	const dispatch = useDispatch();
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const [selectedOrder, setSelectedOrder] = useState(null);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState('');
	const [nextAction, setNextAction] = useState('');
	const [orderDetailsOpen, setOrderDetailsOpen] = useState(false);
	const isMobile = useMediaQuery('(max-width:600px)');
	const ordersByDate = useSelector(selectOrdersByDate);
	const loading = useSelector((state) => state.orders.loadingOrders);
	const error = useSelector((state) => state.orders.errorOrders);

	const [paginationModel, setPaginationModel] = useState({
		page: 0,
		pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
	});
	const [advancedPagination, setAdvancedPagination] = useState({
		page: 0,
		pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
		hasMore: false,
	});
	const [advancedFetching, setAdvancedFetching] = useState(false);


	const fetchAdvancedPage = async ({pageIndex, pageSize}) => {
		const vendor = getVendorId();
		if (!vendor) {
			navigate('/login', {replace: true});
			return [];
		}
		const sanitizedInput = sanitizeAdvancedSearchInputs({
			...filters,
			orderStatus: Array.isArray(filters.orderStatus) && filters.orderStatus.length > 0
				? filters.orderStatus
				: Array.isArray(filters.selectedStatus) ? filters.selectedStatus : [],
		});
		const criteria = buildAdvancedSearchRequest({
			...sanitizedInput,
			selectedStatus: sanitizedInput.orderStatus,
		});
		if (isAdvancedSearchRequestEmpty(criteria)) {
			return [];
		}
		const response = await fetch(
			`${API_BASE_URL}/storefront/orm/${encodeURIComponent(vendor)}/orders/search?pageNumber=${pageIndex + 1}&pageSize=${pageSize}`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-Session-Token': sessionToken(),
				},
				body: JSON.stringify(criteria),
			}
		);
		if (!response.ok) {
			const errorText = await response.text().catch(() => '');
			throw new Error(`Failed to search orders (${response.status}): ${errorText}`);
		}
		const payload = await response.json().catch(() => null);
		const normalized = normalizeAdvancedSearchResponse(payload);
		return filterAdvancedResults(normalized, sanitizedInput);
	};


	useEffect(() => {
		if (selectedDate && selectedDate.isValid()) {
			const currentVendorId = getVendorId();
			if (!currentVendorId) {
				navigate('/login', {replace: true});
				return;
			}
			dispatch(fetchOrdersByDate({date: selectedDate.format('YYYY-MM-DD')}));
		}
	}, [selectedDate, dispatch, navigate]);

	const dateKey = selectedDate.format('YYYY-MM-DD');
	const ordersData = ordersByDate[dateKey];
	const oldReduxOrders = ordersData ? ordersData.orders : [];

	const advancedSearchActive = Array.isArray(searchResults);

	useEffect(() => {
		if (!advancedSearchActive) {
			setAdvancedPagination({
				page: 0,
				pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
				hasMore: false,
			});
			setPaginationModel({page: 0, pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE});
			return;
		}

		setPaginationModel({page: 0, pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE});
		setAdvancedPagination({
			page: 0,
			pageSize: ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
			hasMore: Array.isArray(searchResults) && searchResults.length === ADVANCED_SEARCH_DEFAULT_PAGE_SIZE,
		});
	}, [advancedSearchActive]);

	useEffect(() => {
		if (!advancedSearchActive) {
			return;
		}
		setAdvancedPagination((prev) => {
			const hasMore = Array.isArray(searchResults) && searchResults.length === prev.pageSize;
			if (prev.hasMore === hasMore) {
				return prev;
			}
			return {...prev, hasMore};
		});
	}, [advancedSearchActive, searchResults]);

	const mergedOrders = advancedSearchActive
		? searchResults.map((o) => ({
			id: o.orderId,
			dateOfOrder: o.orderDate,
			customerName: o.customerName || o.buyerName,
			numberOfItems: o.itemCount,
			totalAmount: o.totalValue,
			orderStatus: o.orderStatus ? o.orderStatus.toUpperCase() : '',
			fulfillmentType: o.fulfillmentType,
			orderNumber: o.orderNumber,
		}))
		: oldReduxOrders.map((o) => ({
			...o,
			orderStatus: o.orderStatus ? o.orderStatus.toUpperCase() : '',
		}));

	useEffect(() => {
		if (advancedSearchActive) {
			setPaginationModel((prev) => (prev.page === 0 ? prev : {...prev, page: 0}));
			setAdvancedPagination((prev) => {
				const next = {
					page: 0,
					pageSize: prev.pageSize,
					hasMore: Array.isArray(searchResults) && searchResults.length === prev.pageSize,
				};
				if (prev.page === next.page && prev.pageSize === next.pageSize && prev.hasMore === next.hasMore) {
					return prev;
				}
				return next;
			});
			return;
		}

		setAdvancedPagination((prev) => {
			if (prev.page === 0 && !prev.hasMore) {
				return prev;
			}
			return {...prev, page: 0, hasMore: false};
		});
		setPaginationModel((prev) => (prev.page === 0 ? prev : {...prev, page: 0}));
	}, [advancedSearchActive, searchResults]);

	const handleViewOrder = (order) => {
		setSelectedOrder(order);
		setOrderDetailsOpen(true);
	};

	const handleCloseOrderDetails = () => {
		setOrderDetailsOpen(false);
		setSelectedOrder(null);
	};

	const getActionLabel = (status) => {
		switch (status) {
			case 'PROCESSING':
				return 'Process Order';
			case 'PICKED':
				return 'Pick Order';
			case 'PACKED':
				return 'Pack Order';
			case 'READY_FOR_PICKUP':
				return 'Ready for Pickup';
			case 'PICKED_UP':
				return 'Confirm Pickup';
			case 'SHIPPED':
				return 'Ship Order';
			case 'OUT_FOR_DELIVERY':
				return 'Mark as Out for Delivery';
			case 'DELIVERED':
				return 'Confirm Delivery';
			default:
				return statusLabels[status] || status;
		}
	};

	const handleNextActionClick = (order) => {
		const nextStatus = getNextAction(order);
		const pickup = isStorePickup(order);

		if (nextStatus === 'PICKED' && order.orderStatus === 'PROCESSING') {
			handleViewOrder(order);
			return;
		}

		if (nextStatus && validateStatusTransition(order.orderStatus, nextStatus, pickup)) {
			setSelectedOrder(order);
			setNextAction(nextStatus);
			setOpenConfirmDialog(true);
		} else {
			setSnackbarMessage(`Cannot transition from ${order.orderStatus} to ${nextStatus}`);
			setSnackbarOpen(true);
		}
	};

	const handleConfirmNextAction = async () => {
		if (!selectedOrder) return;
		const pickup = isStorePickup(selectedOrder);
		const cs = selectedOrder.orderStatus;
		const ns = nextAction;
		if (!validateStatusTransition(cs, ns, pickup)) {
			setSnackbarMessage(`Invalid status transition from ${cs} to ${ns}`);
			setSnackbarOpen(true);
			setOpenConfirmDialog(false);
			return;
		}
		try {
			setOpenConfirmDialog(false);
			setSelectedOrder(null);
			setNextAction('');

			setSnackbarMessage(`Updating order status...`);
			setSnackbarOpen(true);

			const currentVendorId = getVendorId();
			if (!currentVendorId) {
				navigate('/login', {replace: true});
				return;
			}

			let endpoint = '';
			const username = getUserName();
			const oid = selectedOrder.id;
			switch (ns) {
				case 'PROCESSING':
					endpoint = buildUrl(`/orders/${oid}/process?username=${username}`, currentVendorId);
					break;
				case 'PICKED':
					endpoint = buildUrl(`/orders/${oid}/pick?username=${username}`, currentVendorId);
					break;
				case 'PACKED':
					endpoint = buildUrl(`/orders/${oid}/pack?username=${username}`, currentVendorId);
					break;
				case 'READY_FOR_PICKUP':
					endpoint = buildUrl(`/orders/${oid}/readyForPickup?username=${username}`, currentVendorId);
					break;
				case 'PICKED_UP':
					endpoint = buildUrl(`/orders/${oid}/pickup?username=${username}`, currentVendorId);
					break;
				case 'SHIPPED':
					endpoint = buildUrl(`/orders/${oid}/ship?username=${username}`, currentVendorId);
					break;
				case 'OUT_FOR_DELIVERY':
					endpoint = buildUrl(`/orders/${oid}/outForDelivery?username=${username}`, currentVendorId);
					break;
				case 'DELIVERED':
					endpoint = buildUrl(`/orders/${oid}/deliver?username=${username}`, currentVendorId);
					break;
				default:
					setSnackbarMessage(`Unknown next status: ${ns}`);
					setSnackbarOpen(true);
					return;
			}
			const response = await fetch(endpoint, {method: 'POST', headers: authHeaders()});
			if (response.ok) {
				setSnackbarMessage(`Order ${oid} status updated to ${ns}`);
				await dispatch(
					fetchOrdersByDate({
						date: dayjs(selectedDate).format('YYYY-MM-DD'),
						forceRefresh: true,
					})
				).unwrap();
			} else {
				const errorData = await response.json();
				setSnackbarMessage(`Failed to update order status: ${errorData.message || 'Unknown error'}`);
			}
		} catch (error) {
			setSnackbarMessage('Error updating order status');
		} finally {
			setSnackbarOpen(true);
		}
	};

	const handleCancelNextAction = () => {
		setOpenConfirmDialog(false);
		setSelectedOrder(null);
		setNextAction('');
	};

	const handleSnackbarClose = () => {
		setSnackbarOpen(false);
	};

	const filteredOrders = mergedOrders.filter((order) => {
		const matchesSearch =
			!searchQuery ||
			order.orderNumber?.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
			order.customerName?.toLowerCase()?.includes(searchQuery.toLowerCase());
		const matchesStatus =
			selectedStatus.length === 0 || selectedStatus.includes(order.orderStatus);
		return matchesSearch && matchesStatus;
	});

	const sortedOrders = filteredOrders.sort(
		(a, b) => new Date(b.dateOfOrder) - new Date(a.dateOfOrder)
	);

	const combinedLoading = loading || advancedFetching || searchLoading;
	const visibleRowCount = advancedSearchActive
		? advancedPagination.page * advancedPagination.pageSize + mergedOrders.length
		: sortedOrders.length;
	const rowCount = advancedSearchActive
		? (advancedPagination.hasMore ? (advancedPagination.page + 2) * advancedPagination.pageSize : visibleRowCount)
		: visibleRowCount;
	const labelDisplayedRows = ({from, to, count}) => {
		if (!advancedSearchActive) {
			const total = count === -1 ? to : count;
			return `${from}-${to} of ${total}`;
		}
		if (mergedOrders.length === 0) {
			return '0-0 of 0';
		}
		const pageStart = advancedPagination.page * advancedPagination.pageSize + 1;
		const pageEnd = pageStart + mergedOrders.length - 1;
		const totalLabel = advancedPagination.hasMore ? `${pageEnd}+` : `${pageEnd}`;
		return `${pageStart}-${pageEnd} of ${totalLabel}`;
	};

	const handlePaginationModelChange = async (newModel) => {
		if (!advancedSearchActive) {
			setPaginationModel(newModel);
			return;
		}

		const {page: desiredPage, pageSize: desiredPageSize} = newModel;
		const pageChanged = desiredPage !== paginationModel.page;
		const pageSizeChanged = desiredPageSize !== paginationModel.pageSize;
		if (!pageChanged && !pageSizeChanged) {
			return;
		}

		const targetPage = pageSizeChanged ? 0 : desiredPage;
		const effectivePageSize = pageSizeChanged ? desiredPageSize : paginationModel.pageSize;

		setAdvancedFetching(true);
		try {
			const results = await fetchAdvancedPage({pageIndex: targetPage, pageSize: effectivePageSize});
			if (results.length === 0 && targetPage > 0) {
				const fallbackResults = await fetchAdvancedPage({pageIndex: 0, pageSize: effectivePageSize});
				if (onAdvancedSearchResultsChange) {
					onAdvancedSearchResultsChange(fallbackResults);
				}
				const fallbackHasMore = fallbackResults.length === effectivePageSize;
				setAdvancedPagination({
					page: 0,
					pageSize: effectivePageSize,
					hasMore: fallbackHasMore,
				});
				setPaginationModel({page: 0, pageSize: effectivePageSize});
				return;
			}

			if (onAdvancedSearchResultsChange) {
				onAdvancedSearchResultsChange(results);
			}
			const hasMore = results.length === effectivePageSize;
			setAdvancedPagination({
				page: targetPage,
				pageSize: effectivePageSize,
				hasMore,
			});
			setPaginationModel({page: targetPage, pageSize: effectivePageSize});
		} catch (error) {
			console.error('Failed to fetch advanced search results:', error);
			setPaginationModel({page: advancedPagination.page, pageSize: advancedPagination.pageSize});
		} finally {
			setAdvancedFetching(false);
		}
	};


	const StyledGridOverlay = styled('div')(({theme}) => ({
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		color: theme.palette.text.secondary,
		'& .empty-box': {
			width: '150px',
			height: '150px',
			border: `2px dashed ${theme.palette.divider}`,
			borderRadius: '8px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '14px',
			backgroundColor: theme.palette.background.paper,
		},
	}));

	function CustomNoResultsOverlay() {
		return (
			<StyledGridOverlay>
				<div className="empty-box">No orders found</div>
			</StyledGridOverlay>
		);
	}

	const columns = [
		{
			field: 'orderNumber',
			headerName: 'Order ID',
			flex: 1,
			minWidth: 200,
		},
		{
			field: 'customerName',
			headerName: 'Customer Name',
			minWidth: 200,
		},
		{
			field: 'dateOfOrder',
			headerName: 'Date of Order',
			flex: 1,
			minWidth: 140,
			type: 'date',
			valueGetter: (params) => new Date(params.value),
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'numberOfItems',
			headerName: 'Items',
			type: 'number',
			flex: 1,
			minWidth: 70,
			align: 'center',
			headerAlign: 'center',
		},
		{
			field: 'totalAmount',
			headerName: 'Total (₹)',
			type: 'number',
			flex: 1,
			minWidth: 75,
			align: 'right',
			headerAlign: 'right',
		},
		{
			field: 'orderStatus',
			headerName: 'Status',
			flex: 1,
			minWidth: 150,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params) => {
				const uiLabel = statusLabels[params.value] || params.value;
				return (
					<Chip
						label={uiLabel}
						sx={getStatusStyles(params.value)}
						variant="outlined"
					/>
				);
			},
		},
		{
			field: 'viewOrder',
			headerName: 'Order Details',
			flex: 1,
			minWidth: 100,
			sortable: false,
			filterable: false,
			align: 'center',
			headerAlign: 'center',
			renderCell: (params) => (
				<Button
					onClick={() => handleViewOrder(params.row)}
					variant="outlined"
					size="small"
					color="secondary"
					sx={{
						textTransform: 'none',
						borderRadius: '50px',
						padding: '4px 16px',
					}}
				>
					View
				</Button>
			),
		},
		{
			field: 'statusAction',
			headerName: 'Next Action',
			flex: 1,
			minWidth: 180,
			sortable: false,
			align: 'center',
			headerAlign: 'center',
			filterable: false,
			renderCell: (params) => {
				const next = getNextAction(params.row);
				if (!next) return null;
				return (
					<Button
						variant="contained"
						size="small"
						onClick={() => handleNextActionClick(params.row)}
						sx={getNextStatusButtonStyles(next)}
					>
						{getActionLabel(next)}
					</Button>
				);
			},
		},
	];

	return (
		<Box sx={{width: '100%', my: isMobile ? 0.6 : 2, px: 1}}>
			<ThemeProvider theme={theme}>
				<Backdrop
					sx={{color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 999}}
					open={combinedLoading}
				>
					<CircularProgress color="inherit"/>
				</Backdrop>

				{isMobile ? (
					sortedOrders.length === 0 ? (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								alignItems: 'center',
								justifyContent: 'center',
								minHeight: 200,
								gap: 1,
								border: '2px dashed',
								borderRadius: 2,
								borderColor: '#bbb',
								color: 'text.secondary',
								textAlign: 'center',
								p: 2, boxShadow: '1px 1px 5px 0px #bbb',
							}}
						>
							<Typography variant="h6" gutterBottom>
								No orders found
							</Typography>
							<Typography variant="body2">
								There are currently no orders to display.
							</Typography>
						</Box>
					) : (
						<Box sx={{display: 'flex', p: 1, flexDirection: 'column', gap: 1.5, borderRadius: 2, boxShadow: '1px 1px 5px 0px #bbb',}}>
							{sortedOrders.map((order) => {
								const uiLabel = statusLabels[order.orderStatus] || order.orderStatus;
								const colorStyles = getStatusStyles(order.orderStatus);
								const next = getNextAction(order);

								return (
									<Card
										key={order.id}
										elevation={3}
										sx={{
											borderRadius: 2,
											overflow: 'hidden',
											position: 'relative',
											width: '100%',
										}}
									>
										<Box
											sx={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: 5,
												height: '100%',
												backgroundColor: colorStyles.backgroundColor,
											}}
										/>
										<CardActionArea onClick={() => handleViewOrder(order)}>
											<CardContent sx={{padding: '8px 8px 8px 16px'}}>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
													}}
												>
													<Typography
														variant="subtitle1"
														sx={{fontWeight: 600, color: 'text.primary'}}
													>
														{order.orderNumber}
													</Typography>
													<Typography variant="caption" sx={{color: 'text.secondary'}}>
														{dayjs(order.dateOfOrder).format('MM/DD/YYYY')}
													</Typography>
												</Box>
												<Typography
													variant="body2"
													sx={{color: 'text.primary', mt: 0.5}}
												>
													{order.customerName}
												</Typography>
												<Box
													sx={{
														display: 'flex',
														justifyContent: 'space-between',
														alignItems: 'center',
														mt: 0.5,
													}}
												>
													<Typography
														variant="caption"
														sx={{fontSize: '0.9rem', color: 'text.secondary'}}
													>
														Items: {order.numberOfItems}
													</Typography>
													<Typography
														variant="caption"
														sx={{
															fontSize: '0.9rem',
															color: 'text.secondary',
															fontWeight: 600,
														}}
													>
														₹{order.totalAmount}
													</Typography>
												</Box>
												<Box
													sx={{
														display: 'flex',
														flexWrap: 'wrap',
														alignItems: 'center',
														justifyContent: 'space-between',
														gap: 1,
														mt: 1.5,
													}}
													onClick={(e) => {
														e.stopPropagation();
													}}
												>
													<Chip
														label={uiLabel}
														sx={getStatusStyles(order.orderStatus)}
														variant="outlined"
													/>
													<Box sx={{display: 'flex', gap: 1}}>
														<Button
															variant="outlined"
															size="small"
															onClick={() => handleViewOrder(order)}
															sx={{
																textTransform: 'none',
																borderRadius: '50px',
																padding: '4px 16px',
																color: '#1976d2',
																borderColor: '#1976d2',
																'&:hover': {
																	backgroundColor: '#e3f2fd',
																	borderColor: '#1565c0',
																},
															}}
														>
															View
														</Button>
														{next && (
															<Button
																variant="contained"
																size="small"
																sx={{...getNextStatusButtonStyles(next), width: 'auto'}}
																onClick={() => handleNextActionClick(order)}
															>
																{getActionLabel(next)}
															</Button>
														)}
													</Box>
												</Box>
											</CardContent>
										</CardActionArea>
									</Card>
								);
							})}
						</Box>
					)
				) : (
					// -------------- DESKTOP VIEW --------------
					<Box
						sx={{
							height: 600,
							width: '100%',
							boxShadow: '2px 2px 5px 0px #bbb',
							borderRadius: 1,
							borderWidth: 2,
							borderColor: '#bbb',
						}}
					>
						<DataGrid
							rows={sortedOrders}
							columns={columns}
							paginationMode={advancedSearchActive ? 'server' : 'client'}
							paginationModel={paginationModel}
							onPaginationModelChange={handlePaginationModelChange}
							rowCount={rowCount}
							pageSizeOptions={[10, 20, 25, 50]}
							loading={combinedLoading}
							components={{
								NoRowsOverlay: CustomNoResultsOverlay,
								Toolbar: CustomToolbar,
							}}
							slotProps={{
								pagination: {labelDisplayedRows},
							}}
							initialState={{
								sorting: {
									sortModel: [{field: 'dateOfOrder', sort: 'desc'}],
								},
							}}
							disableRowSelectionOnClick
							sx={{
								'& .MuiDataGrid-columnHeaders': {
									backgroundColor: '#f5f5f5',
								},
								'& .MuiDataGrid-row:hover': {
									backgroundColor: '#f9f9f9',
								},
								'& .MuiDataGrid-cell': {
									borderBottom: '1px solid #e0e0e0',
								},
								'& .MuiDataGrid-footerContainer': {
									backgroundColor: '#f5f5f5',
								},
								minWidth: 440,
							}}
						/>
					</Box>
				)}
				{error && (
					<Box sx={{mt: 2}}>
						<MuiAlert severity="error">{error}</MuiAlert>
					</Box>
				)}
				<OrderDetails
					open={orderDetailsOpen}
					onClose={handleCloseOrderDetails}
					order={selectedOrder}
					selectedDate={selectedDate}
				/>

				<Dialog open={openConfirmDialog} onClose={handleCancelNextAction}>
					<DialogTitle>Confirm {getActionLabel(nextAction)}</DialogTitle>
					<DialogContent>
						<DialogContentText>
							Are you sure you want to <strong>{getActionLabel(nextAction)}</strong> for Order ID{' '}
							<strong>{selectedOrder?.id}</strong>?
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleCancelNextAction} color='secondary'>
							Cancel
						</Button>
						<Button color='secondary' onClick={handleConfirmNextAction} autoFocus>
							Confirm
						</Button>
					</DialogActions>
				</Dialog>

				<Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
					<MuiAlert onClose={handleSnackbarClose} severity="success" sx={{width: '100%'}}>
						{snackbarMessage}
					</MuiAlert>
				</Snackbar>
			</ThemeProvider>
		</Box>
	);
};

export default OrderList;
